import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  AlertCircle,
  CheckCircle,
  Clock,
  Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import taskService from '@/services/taskService';
import TaskCardModal from './TaskCardModal';

const TaskCalendar = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showCardModal, setShowCardModal] = useState(false);

  useEffect(() => {
    loadCalendarData();
  }, [currentDate]);

  const loadCalendarData = async () => {
    setLoading(true);
    try {
      // Get all cards with due dates
      const allCards = await taskService.getAllCards();
      
      // Filter only cards with due dates
      const cardsWithDueDates = allCards.filter(card => card.due_date);
      setCards(cardsWithDueDates);
    } catch (error) {
      console.error('Erro ao carregar calendário:', error);
      setCards([]);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    
    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    
    // Add days of month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const getCardsForDate = (date) => {
    if (!date) return [];
    
    return cards.filter(card => {
      const cardDate = new Date(card.due_date);
      return (
        cardDate.getDate() === date.getDate() &&
        cardDate.getMonth() === date.getMonth() &&
        cardDate.getFullYear() === date.getFullYear()
      );
    });
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleCardClick = (card) => {
    setSelectedCard(card);
    setShowCardModal(true);
  };

  const handleCardUpdated = async (updatedCard) => {
    if (updatedCard) {
      setCards(prevCards => prevCards.map(c => c._id === updatedCard._id ? updatedCard : c));
    } else {
      await loadCalendarData();
    }
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isPast = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
  };

  const days = getDaysInMonth(currentDate);
  const monthName = currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Calendário de Tarefas</h1>
          <p className="text-gray-600 mt-1">Visualize tarefas por data de vencimento</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={goToToday}>
            Hoje
          </Button>
          <Button onClick={() => navigate('/portal/tasks')}>
            Ver Boards
          </Button>
        </div>
      </div>

      {/* Calendar Navigation */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold capitalize">{monthName}</h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={previousMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={nextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2">
            {/* Day headers */}
            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((day) => (
              <div key={day} className="text-center font-semibold text-sm text-gray-600 py-2">
                {day}
              </div>
            ))}

            {/* Calendar days */}
            {days.map((date, index) => {
              const dateCards = getCardsForDate(date);
              const isCurrentDay = isToday(date);
              const isPastDay = isPast(date);

              return (
                <div
                  key={index}
                  className={`
                    min-h-[100px] p-2 border rounded-lg
                    ${!date ? 'bg-gray-50' : ''}
                    ${isCurrentDay ? 'border-blue-600 border-2 bg-blue-50' : ''}
                    ${isPastDay && date ? 'bg-gray-50' : ''}
                    ${date && !isCurrentDay && !isPastDay ? 'hover:bg-gray-50 cursor-pointer' : ''}
                  `}
                >
                  {date && (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <span
                          className={`text-sm font-medium ${
                            isCurrentDay ? 'text-blue-600 font-bold' : 'text-gray-700'
                          }`}
                        >
                          {date.getDate()}
                        </span>
                        {dateCards.length > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {dateCards.length}
                          </Badge>
                        )}
                      </div>

                      <div className="space-y-1">
                        {dateCards.slice(0, 3).map((card) => {
                          const isOverdue = isPastDay && !card.is_completed;
                          
                          return (
                            <div
                              key={card._id}
                              className={`
                                text-xs p-1 rounded cursor-pointer hover:opacity-80
                                ${card.is_completed ? 'bg-green-100 text-green-800' : ''}
                                ${isOverdue ? 'bg-red-100 text-red-800' : ''}
                                ${!card.is_completed && !isOverdue ? 'bg-blue-100 text-blue-800' : ''}
                              `}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCardClick(card);
                              }}
                              title={card.title}
                            >
                              <div className="flex items-center gap-1">
                                {card.is_completed ? (
                                  <CheckCircle className="h-3 w-3" />
                                ) : isOverdue ? (
                                  <AlertCircle className="h-3 w-3" />
                                ) : (
                                  <Clock className="h-3 w-3" />
                                )}
                                <span className="truncate">{card.title}</span>
                              </div>
                            </div>
                          );
                        })}
                        {dateCards.length > 3 && (
                          <div className="text-xs text-gray-500 text-center">
                            +{dateCards.length - 3} mais
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-6 justify-center">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-100 border border-blue-300 rounded"></div>
              <span className="text-sm text-gray-600">Pendente</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
              <span className="text-sm text-gray-600">Concluído</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
              <span className="text-sm text-gray-600">Atrasado</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-50 border-2 border-blue-600 rounded"></div>
              <span className="text-sm text-gray-600">Hoje</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Card Modal */}
      <TaskCardModal
        isOpen={showCardModal}
        onClose={() => {
          setShowCardModal(false);
          setSelectedCard(null);
        }}
        card={selectedCard}
        boardId={selectedCard?.board_id}
        onCardUpdated={handleCardUpdated}
      />
    </div>
  );
};

export default TaskCalendar;
